//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useContext, useState } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles, Grid } from '@material-ui/core';
import ArrowRightAltIcon from '@material-ui/icons/ArrowForward';
import ArrowBack from '@material-ui/icons/ArrowBack';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { LayoutContext } from '../../../common/contexts';

import { setClassNameInDefns } from '../../../common/definitionHelper';
// import AfitdTypography from '../../../common/elemental/AfitdTypography';
// import AfitdTextField from '../../../common/elemental/AfitdTextField';
import AfitdButton from '../../../common/elemental/AfitdButton';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTextField from '../../../common/elemental/AfitdTextField';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import BorderLinearProgress from '../BorderLinearProgress';

// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';
// import _set from 'lodash/set';
// import _includes from 'lodash/includes';

import AfitdRatingCounter from '../../../common/elemental/AfitdRatingCounter';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    exampleWithNoPropsOnlyStyles: { color: 'red', marginTop: 30, marginBottom: 40 },
    nonReactElementExample: { color: 'blue', marginTop: 50, cursor: 'pointer' },
    container: {
        width: '100%',
        // height: '640px',
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '48px 112px 48px 112px',
        [theme.breakpoints.down('md')]: { padding: '48px' },
        [theme.breakpoints.down('sm')]: { padding: '44px 0px' },
    },
    heading: {
        fontSize: '26px',
        fontWeight: 'bold',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            fontSize: '18px',
        },
    },
    fieldLabel: {
        fontWeight: 'bold',
        fontSize: '14px',
        lineHeight: '16px',
        marginTop: '20px',
    },
    progressGrid: {
        textAlign: 'center',
    },
    gridBody: {
        marginTop: '38px',
        maxHeight: '400px',
        overflowY: 'overlay',
        '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
            background: 'none',
        },
        '&::-webkit-scrollbar': {
            width: '8px',
            backgroundColor: '#F5F5F5',
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: 'rgba(0,0,0,0.2)',
        },
    },
    ratingContainer: { display: 'flex', justifyContent: 'center', marginTop: '38px' },
    ratingItem: { width: 645 },
    gridBody2: {
        paddingRight: 8,
        // padding: '0px 85px 0px 75px',
        // [theme.breakpoints.down('lg')]: { padding: '0px 65px 0px 55px' },
        // [theme.breakpoints.down('md')]: { padding: 0, paddingRight: 8 },
        marginTop: '38px',
        maxHeight: '400px',
        overflowY: 'overlay',
        '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
            background: 'none',
        },
        '&::-webkit-scrollbar': {
            width: '8px',
            backgroundColor: '#F5F5F5',
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: 'rgba(0,0,0,0.2)',
        },
    },
    nextButton: {
        textAlign: 'right',
        marginTop: '18px',
    },
    backButton: {
        textAlign: 'left',
        marginTop: '18px',
    },
    newRowContainer: { marginTop: 40, [theme.breakpoints.down('sm')]: { marginTop: 20 } },
    newRow: {
        color: '#1D8A8B',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
    },
    removeField: {
        marginTop: '65px',
        color: '#111E14',
        opacity: '0.6',
        cursor: 'pointer',
        [theme.breakpoints.down('sm')]: { marginTop: '5px' },
    },
    removeButtonContainer: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            paddingRight: '8px',
            // display: 'flex',
            justifyContent: 'flex-end',
        },
    },
    progressGridContainerSm: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        paddingTop: 34,
    },
    gridBody2Container: { width: 645 },
    gridBody2Root: { display: 'flex', justifyContent: 'center' },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, isDirty, pageId, totalSteps, handleBack, jsCommentMoreHelpful, handleChangeHelpfulComment, errors } = params;
    const { jsCommentMoreHelpfulError } = errors;
    // const isDirty =
    //     formData.companyName === '' && state === '' && country === '' && city === '' && formData.emailAddress === '' && formData.phone === '' && formData.contactName === '';

    const defns = {
        helloText: { variant: 'h4' },
        textFieldExample: { value: 'Hello TextField' },
        nonReactElementExample: {
            onClick: evt => {
                alert('Clicked!');
            },
        },
        gridBody: {
            container: true,
            spacing: 2,
            alignItems: 'center',
        },
        gridBody2: {
            container: true,
            spacing: 2,
            alignItems: 'center',
        },
        itemGrid: { item: true, xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        itemGrid2: { item: true, xl: 3, lg: 3, md: 3, sm: 6, xs: 6 },
        progressGrid: { item: true, md: 2, sm: 6, xs: 6 },

        footerGridItem: {
            xl: 5,
            lg: 5,
            md: 5,
            sm: 6,
            xs: 6,
        },
        progress: {
            variant: 'determinate',
            value: parseFloat((pageId / totalSteps) * 100).toFixed(2),
        },

        nextButton: {
            color: 'primary',
            disabled: !isDirty,
        },
        backButton: {
            onClick: handleBack,
        },
        commentMoreHelpful: {
            name: 'commentMoreHelpful',
            placeholder: 'Please type here...',
            onChange: handleChangeHelpfulComment,
            value: jsCommentMoreHelpful,
            error: jsCommentMoreHelpfulError,
            helperText: jsCommentMoreHelpfulError ? 'Comment is required' : undefined,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JSStep2Page = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));
    // const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { scrollToPageTop, jsHelpfulRating, setJsHelpfulRating, jsCommentMoreHelpful, setJsCommentMoreHelpful, pageId, setPageID, totalSteps, stepDirtyList, setStepDirtyList } =
        layoutContext;
    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [showComment, setShowComment] = useState(jsCommentMoreHelpful !== '');
    const [errors, setErrors] = useState({ jsCommentMoreHelpfulError: false });

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const step2IsDirty = stepDirtyList?.step2;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // on mount, scroll to the top of the page
    useEffect(() => {
        scrollToPageTop();
    }, [scrollToPageTop]);

    useEffect(() => {
        if (step2IsDirty) setIsDirty(true);
    }, [step2IsDirty]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeHelpfulComment = React.useCallback(
        evt => {
            const { value } = evt.target;
            setJsCommentMoreHelpful(value);
            setStepDirtyList(prev => ({ ...prev, step2: true }));
            setErrors(prev => ({ ...prev, jsCommentMoreHelpfulError: !value }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [jsCommentMoreHelpful],
    );

    const handleClickNextButton = async () => {
        // let newErrors = { ...errors };

        if (showComment) {
            // _set(newErrors, 'jsCommentMoreHelpfulError', !jsCommentMoreHelpful);
            // setErrors(newErrors);
            // if (!_includes(newErrors, true)) setPageID(pageId + 1);
            setPageID(pageId + 1);
        } else {
            if (jsHelpfulRating < 5) {
                setShowComment(true);
            } else {
                setShowComment(false);
                setJsCommentMoreHelpful('');
                setPageID(pageId + 1);
            }
        }
    };

    const handleBack = () => {
        if (showComment) {
            setShowComment(false);
        } else {
            setPageID(pageId - 1);
        }
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        isDirty,
        pageId,
        setPageID,
        totalSteps,
        jsCommentMoreHelpful,
        handleChangeHelpfulComment,
        handleBack,
        errors,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            {!showComment ? (
                <>
                    <div {...defns.heading}>Based on your usage, how helpful has "A Foot In The Door" been towards your job search?</div>
                    <div {...defns.ratingContainer}>
                        <div {...defns.ratingItem}>
                            <AfitdRatingCounter
                                setIsDirty={isDirty => setStepDirtyList(prev => ({ ...prev, step2: isDirty }))}
                                rating={jsHelpfulRating}
                                setRating={setJsHelpfulRating}
                                lefttext="Not very helpful"
                                rightText="Extremely helpful"
                            />
                        </div>
                    </div>
                </>
            ) : (
                <div {...defns.root}>
                    <div {...defns.heading}>Can you please tell us how our platform can be more helpful towards your job search?</div>

                    <Grid {...defns.gridBody}>
                        <Grid {...defns.itemGrid}>
                            <AfitdTextField
                                {...defns.commentMoreHelpful}
                                color="secondary"
                                id="commentMoreHelpful"
                                InputProps={classes.multiline}
                                multiline={true}
                                rows={8}
                                placeholder="Enter feedback here..."
                            />
                        </Grid>
                    </Grid>
                </div>
            )}

            <div {...defns.gridBody2Root}>
                <div {...defns.gridBody2Container}>
                    <Grid {...defns.gridBody2}>
                        <Grid {...defns.footerGridItem}>
                            <div {...defns.backButton}>
                                <AfitdButton {...defns.backButton}>
                                    <ArrowBack fontSize="small" />
                                    &nbsp; Back {` `}
                                </AfitdButton>
                            </div>
                        </Grid>
                        {smMatches ? null : (
                            <Grid {...defns.progressGrid}>
                                <p>
                                    {pageId} of {totalSteps}
                                </p>
                                <BorderLinearProgress {...defns.progress} />
                            </Grid>
                        )}

                        <Grid {...defns.footerGridItem}>
                            <div {...defns.nextButton}>
                                <AfitdButton {...defns.nextButton} onClick={handleClickNextButton}>
                                    Next {` `}
                                    &nbsp;
                                    <ArrowRightAltIcon fontSize="small" />
                                </AfitdButton>
                            </div>
                        </Grid>
                        {smMatches && (
                            <div {...defns.progressGridContainerSm}>
                                <Grid {...defns.progressGrid}>
                                    <p>
                                        {pageId} of {totalSteps}
                                    </p>
                                    <BorderLinearProgress {...defns.progress} />
                                </Grid>
                            </div>
                        )}
                    </Grid>
                </div>
            </div>
            {/* </Container> */}
        </div>
    );
};

export default JSStep2Page;
